import { api } from '@/lib/api';
import type {
  RmaOrder,
  RmaListResponse,
  RmaFilters,
  RmaStatus,
  CreateRmaInput,
  AddRmaItemInput,
  UpdateRmaItemInput,
  RmaItem,
  RmaActivityEntry,
  GenerateLabelInput,
  RmaQuoteContext,
  CreateRmaFromQuoteInput,
  RmaRatesResponse,
} from './types';

export async function getRmaList(filters: RmaFilters = {}): Promise<RmaListResponse> {
  const params: Record<string, string | number> = {
    pageNumber: filters.pageNumber ?? 0,
    pageLength: filters.pageLength ?? 25,
    sortKey: filters.sortKey ?? 'createdAt',
    sortDirection: filters.sortDirection ?? -1,
  };
  if (filters.status) params.status = filters.status;
  if (filters.accountId) params.accountId = filters.accountId;
  if (filters.search) params.search = filters.search;

  return api.get<RmaListResponse>('/v4/rma', params);
}

export async function getRma(id: string): Promise<RmaOrder> {
  return api.get<RmaOrder>(`/v4/rma/${id}`);
}

export async function createRma(data: CreateRmaInput): Promise<RmaOrder> {
  return api.post<RmaOrder>('/v4/rma', data);
}

export async function updateRma(id: string, data: Partial<CreateRmaInput>): Promise<RmaOrder> {
  return api.patch<RmaOrder>(`/v4/rma/${id}`, data);
}

export async function updateRmaStatus(id: string, status: RmaStatus): Promise<RmaOrder> {
  return api.patch<RmaOrder>(`/v4/rma/${id}/status`, { status });
}

export async function generateRmaLabel(id: string, data: GenerateLabelInput = {}): Promise<RmaOrder> {
  return api.post<RmaOrder>(`/v4/rma/${id}/generate-label`, data);
}

/** Side-by-side return-label rates: Canada Post vs Planète Courrier (draft-first PC). */
export async function getRmaRates(id: string, weight?: number): Promise<RmaRatesResponse> {
  const params: Record<string, string | number> = {};
  if (weight) params.weight = weight;
  return api.get<RmaRatesResponse>(`/v4/rma/${id}/rates`, params);
}

export function getRmaLabelUrl(id: string): string {
  return `/api/v4/rma/${id}/label`;
}

export async function deleteRma(id: string): Promise<void> {
  return api.delete(`/v4/rma/${id}`);
}

export async function addRmaItem(rmaId: string, data: AddRmaItemInput): Promise<RmaItem> {
  return api.post<RmaItem>(`/v4/rma/${rmaId}/items`, data);
}

export async function removeRmaItem(rmaId: string, itemId: string): Promise<void> {
  return api.delete(`/v4/rma/${rmaId}/items/${itemId}`);
}

export async function updateRmaItem(rmaId: string, itemId: string, data: UpdateRmaItemInput): Promise<RmaItem> {
  return api.patch<RmaItem>(`/v4/rma/${rmaId}/items/${itemId}`, data);
}

export async function getRmaActivity(rmaId: string): Promise<RmaActivityEntry[]> {
  return api.get<RmaActivityEntry[]>(`/v4/rma/${rmaId}/activity`);
}

// --- Quote -> RMA (auto-populate) -------------------------------------------

export async function getRmaQuoteContext(quoteId: string): Promise<RmaQuoteContext> {
  return api.get<RmaQuoteContext>(`/v4/rma/quote-context/${quoteId}`);
}

export async function createRmaFromQuote(data: CreateRmaFromQuoteInput): Promise<RmaOrder> {
  return api.post<RmaOrder>('/v4/rma/from-quote', data);
}
